import { useEffect, useRef, useState } from 'react';
import { Center, Loader } from '@mantine/core';
import { useAuth } from '@workos-inc/authkit-react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { FLAG_WAIT_MS, useFlagsSettled, useFlushFlags, useTrialAutoStartEnabled } from '../lib/featureFlags';
import { trialAutoStartCachedOn } from '../lib/ldFlagCache';
import { resolveLandingRedirect } from '../lib/routing';
import {
  getLoginHint,
  markSignupAttempted,
  peekTrialIntent,
  trialSourceFields,
} from '../lib/trialIntent';
import { logEvent, useTrackFunnel, FUNNEL } from '../lib/telemetry';
import { errorType } from '../lib/telemetry/errorType';
import { LandingView } from './LandingView';
import { tokens } from '../theme';

function LoaderScreen() {
  return (
    <Center h="100vh" style={{ background: tokens.navy }} role="progressbar" aria-label="Loading">
      <Loader size="xl" color="cloudhouseGreen" />
    </Center>
  );
}

export function Landing() {
  const { signIn, signUp, user, isLoading, organizationId } = useAuth();
  const trialIntentEnabled = useTrialAutoStartEnabled();
  const flagsSettled = useFlagsSettled();
  const flushFlags = useFlushFlags();
  const trackFunnel = useTrackFunnel();
  const [params] = useSearchParams();
  // Peeked, not read: whether to hold the loader is decided before the release flag is known, and
  // an expired intent is only worth reporting once the flag says the trial flow is live.
  const [storedIntent, setStoredIntent] = useState(peekTrialIntent);
  // With the release flag off (or not yet known) the stored intent is inert: the trial variant
  // comes from the URL exactly as before the intent flow, with no automatic sign-up and no hint.
  const intent = trialIntentEnabled ? storedIntent : null;
  const [signupBusy, setSignupBusy] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [flagWaitExpired, setFlagWaitExpired] = useState(false);
  // Read once: whether LaunchDarkly will first serve the trial flag on, from its own cache.
  const [cachedOn] = useState(trialAutoStartCachedOn);
  const autoSignupStarted = useRef(false);

  const autoSignup = intent !== null && intent.attemptedAt === undefined && !params.has('error');
  // Until the flags settle (or the wait expires) a late "on" still starts the automatic sign-up.
  const waitingOnFlags = autoSignup || (storedIntent !== null && !flagsSettled);

  useEffect(() => {
    // Only count the exposure once auth has settled — a still-loading returning
    // user has no `user` yet and must not emit a spurious funnel event.
    if (!isLoading && !user && !autoSignup) trackFunnel(FUNNEL.LANDING_VIEWED);
  }, [isLoading, user, autoSignup, trackFunnel]);

  useEffect(() => {
    // Counted from the moment auth has settled, so a slow session restore does not eat the wait.
    if (!waitingOnFlags || isLoading) return;
    const timer = setTimeout(() => {
      if (autoSignupStarted.current) return;
      setFlagWaitExpired(true);
      logEvent('cloudhouse.enterprise.trial.flag_wait_expired', 'Gave up waiting for LaunchDarkly on a trial link',
        { flags_settled: flagsSettled, cached_on: cachedOn }, 'INFO');
    }, FLAG_WAIT_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one wait per hold; the fields are read when it expires
  }, [waitingOnFlags, isLoading]);

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      setStoredIntent(peekTrialIntent());
      setSignupBusy(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  async function startSignUp() {
    setSignupBusy(true);                 // stays disabled through the redirect
    setSignupError(null);
    if (intent) {
      markSignupAttempted();
      trackFunnel(FUNNEL.TRIAL_SIGNUP_STARTED, trialSourceFields(intent));
    } else {
      trackFunnel(FUNNEL.CTA_CLICKED);
    }
    try {
      if (intent) {
        // Only the trial path waits on the flush, so the attributed stage lands before the redirect.
        await flushFlags();
        await signUp({ loginHint: getLoginHint() ?? undefined });
      } else {
        await signUp();
      }
      trackFunnel(FUNNEL.REDIRECT_INITIATED);
      // Success → navigation to AuthKit is in flight. Do NOT re-enable (a timer
      // would allow a second signUp() before unload). Re-enable only on failure.
    } catch (e) {
      setSignupError('Something went wrong starting signup — please try again');
      trackFunnel(FUNNEL.FAILED);
      logEvent('cloudhouse.enterprise.signup.failed', 'AuthKit signUp() threw', {
        'error.type': errorType(e),
        error_message: (e as Error).message?.slice(0, 300), // bounded; kept for existing queries
      }, 'ERROR');
      setSignupBusy(false);
    }
  }

  useEffect(() => {
    if (isLoading || user || !autoSignup || flagWaitExpired || autoSignupStarted.current) return;
    autoSignupStarted.current = true;
    void startSignUp();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires once per mount; startSignUp reads the settled intent
  }, [isLoading, user, autoSignup, flagWaitExpired]);

  if (isLoading) return <LoaderScreen />;
  if (user) return <Navigate to={resolveLandingRedirect(organizationId ?? null)} replace />;

  // The loader holds only where the trial page would otherwise flash before an automatic sign-up: the
  // flag already reads on, or this browser's LaunchDarkly cache says it will. A browser with nothing
  // cached gets main's trial Landing at once, as with the flag off.
  const holdForIntent = storedIntent !== null && signupError === null && !flagWaitExpired && waitingOnFlags
    && (autoSignup || cachedOn);
  if (holdForIntent) return <LoaderScreen />;

  return (
    <LandingView
      signupBusy={signupBusy}
      signupError={signupError}
      onSignIn={() => signIn()}
      onSignUp={startSignUp}
      guardianTrial={trialIntentEnabled ? storedIntent !== null : params.get('intent') === 'guardian-trial'}
    />
  );
}
